import { Check, Video } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { ImageWithLoader } from '@/components/ui/image-with-loader';
import { Label } from '@/components/ui/label';
import { useTranslations } from '@/hooks/use-translations';
import { cn } from '@/lib/utils';
import { AssetViewer } from '@/modules/asset-viewer';
import { ConfirmationModal, useConfirmationModal } from '@/modules/confirmation-modal';
import { parseHtml } from '@/utils/html-parser';

import { QuestionSlideProps, Asset, SlideItemType } from '../../types';
import { InfoCard } from './components/InfoCard';
import {
  doesMasterClassQuestionHaveACorrectAnswer,
  isMasterClassAnswerCorrect,
} from '../../utils/answer-validator';
import { getAssetsByRefs, getAssetUrl } from '../../utils/asset-loader';
import { getGridColumns } from '../../utils/grid-columns';
import { resolveItemAssets } from '../../utils/item-asset-resolver';

export const QuestionSlide = ({
  slide,
  assets,
  assetsBaseUrl,
  answerMap,
  onAnswerSelected,
  onNext,
  correctAttempts,
  incorrectAttempts,
  caseConfig,
}: QuestionSlideProps) => {
  const { t } = useTranslations();
  const masterclassSlideHasAObtainableCorrectAnswer = doesMasterClassQuestionHaveACorrectAnswer(
    caseConfig,
    slide.id
  );
  // NOTE the last answer given for this question
  const previousCorrectAnswerId = correctAttempts.get(slide.id)?.[-1] || null;
  const [selectedAnswerId, setSelectedAnswerId] = useState<string | null>(previousCorrectAnswerId);
  const [isValidated, setIsValidated] = useState(false);
  const [viewerState, setViewerState] = useState<{
    isOpen: boolean;
    assets: Asset[];
    initialIndex: number;
    title?: string;
  } | null>(null);

  // Reset states when slide changes
  useEffect(() => {
    const currentPreviousAnswerId = correctAttempts.get(slide.id)?.[0] || null;
    // Handle both string and string[] answers

    setSelectedAnswerId(currentPreviousAnswerId || null);
    // NOTE: If there is a previous correct answer, or if (the slide has no correct answers at all && he has an incorrect attempt),
    setIsValidated(
      !!currentPreviousAnswerId ||
        (!masterclassSlideHasAObtainableCorrectAnswer && incorrectAttempts[slide.id]?.length > 0)
    );
  }, [slide.id, correctAttempts, incorrectAttempts, masterclassSlideHasAObtainableCorrectAnswer]);

  const {
    isOpen: showConfirmModal,
    selectedAnswerText,
    showConfirmation,
    handleConfirm,
    handleCancel,
  } = useConfirmationModal();

  // Convert saved incorrect attempts to Set for this slide
  const questionIncorrectAttempts = useMemo(
    () => new Set(incorrectAttempts[slide.id] || []),
    [slide.id, incorrectAttempts]
  );

  const handleAnswerToggle = (answerId: string) => {
    // Block if already validated, or if this is an incorrect attempt
    if (isValidated || previousCorrectAnswerId || questionIncorrectAttempts.has(answerId)) return;

    // If already selected, deselect; otherwise, select
    if (selectedAnswerId === answerId) {
      setSelectedAnswerId(null);
    } else {
      setSelectedAnswerId(answerId);
      const answerText = answerItems.find((item) => item.id === answerId)?.title || '';

      const correct = isCorrect(answerId);

      // Show confirmation modal with validation logic
      showConfirmation(answerText, () => {
        if (correct || !masterclassSlideHasAObtainableCorrectAnswer) {
          // Correct answer: mark as validated
          setIsValidated(true);

          const videoRef = hasVideoFeedback(answerId) ? answerMap[answerId].videoRef! : undefined;

          void onAnswerSelected(answerId, correct, videoRef);
        } else {
          // Incorrect answer: allow other attempts
          // Do NOT call setIsValidated(true) to allow other attempts
          setSelectedAnswerId(null);

          const videoRef = hasVideoFeedback(answerId) ? answerMap[answerId].videoRef! : undefined;

          void onAnswerSelected(answerId, correct, videoRef);
        }
      });
    }
  };

  const isCorrect = (answerId: string) => {
    return isMasterClassAnswerCorrect(caseConfig, slide.id, [answerId]);
  };

  const hasVideoFeedback = (answerId: string) => !!answerMap[answerId]?.videoRef;

  // Separate info items from answer items
  const infoItems = slide.items.filter((item) => item.type === SlideItemType.INFO);
  const answerItems = slide.items.filter((item) => item.type === SlideItemType.ANSWER_SELECTABLE);

  const referenceDocumentsGridCols = getGridColumns(infoItems.length);

  const handleOpenViewer = (itemAssets: Asset[], initialIndex: number) => {
    setViewerState({
      isOpen: true,
      assets: itemAssets,
      initialIndex,
    });
  };

  const title = useMemo(() => {
    // NOTE: if validated it means it's a correct answer
    if (isValidated) {
      return t('caseViewer.question.answerResults');
    }
    if (questionIncorrectAttempts.size > 0) {
      return t('caseViewer.question.tryanother');
    }
    return t('caseViewer.question.selectAnswer');
  }, [t, isValidated, questionIncorrectAttempts]);

  return (
    <>
      <div className="flex h-full w-full items-center justify-center overflow-y-auto px-2 sm:px-4">
        <div className="w-full max-w-5xl py-4 pb-6 sm:py-6">
          {/* Answer options */}
          <div className="mb-4 sm:mb-6">
            <h2 className="mb-4 font-heading text-lg text-livelinx-text sm:text-xl">{title}</h2>
            <div className="space-y-3 sm:space-y-4">
              {answerItems.map((item) => {
                const itemAssets = getAssetsByRefs(item.assetRefs, assets);
                const isSelected = selectedAnswerId === item.id;
                const isIncorrectAttempt =
                  masterclassSlideHasAObtainableCorrectAnswer &&
                  questionIncorrectAttempts.has(item.id);
                const isCorrectAnswer = isCorrect(item.id);

                // Feedback logic
                const showNeutralFeedback =
                  isValidated &&
                  !masterclassSlideHasAObtainableCorrectAnswer &&
                  questionIncorrectAttempts.has(item.id);
                const showCorrectFeedback =
                  masterclassSlideHasAObtainableCorrectAnswer && isValidated && isCorrectAnswer;
                const showIncorrectFeedback =
                  masterclassSlideHasAObtainableCorrectAnswer &&
                  (isIncorrectAttempt || (isValidated && !isCorrectAnswer));

                const hasAssets = itemAssets.length > 0;
                const hasVideo = hasVideoFeedback(item.id);

                return (
                  <div
                    key={item.id}
                    onClick={() =>
                      !isValidated &&
                      !previousCorrectAnswerId &&
                      !isIncorrectAttempt &&
                      handleAnswerToggle(item.id)
                    }
                    className={cn(
                      'relative w-full rounded-lg border-2 p-4 text-left transition-all duration-300 sm:p-5',
                      // Incorrect attempt: grayed out and not clickable
                      isIncorrectAttempt &&
                        'cursor-not-allowed border-red-500 bg-red-50 opacity-60 ring-2 ring-red-500',
                      // Otherwise, normal behavior
                      !isIncorrectAttempt &&
                        !isValidated &&
                        !previousCorrectAnswerId &&
                        'cursor-pointer active:scale-95',
                      (isValidated || previousCorrectAnswerId) && 'cursor-not-allowed',
                      isSelected &&
                        !isValidated &&
                        !isIncorrectAttempt &&
                        'border-livelinx-purple100 bg-livelinx-purple50 shadow-blue-shadow',
                      showCorrectFeedback && 'border-green-500 bg-green-50 ring-2 ring-green-500',
                      showIncorrectFeedback &&
                        'border-red-500 bg-red-50 opacity-60 ring-2 ring-red-500',
                      showNeutralFeedback && 'border-blue-500 bg-blue-50 ring-2 ring-blue-500',
                      !isSelected &&
                        !isIncorrectAttempt &&
                        !showCorrectFeedback &&
                        !showIncorrectFeedback &&
                        !showNeutralFeedback &&
                        'border-border bg-card md:hover:border-livelinx-purple100 md:hover:shadow-blue-shadow'
                    )}
                  >
                    {/* Badges Container */}
                    <div className="absolute right-3 top-3 flex gap-2">
                      {/* Alternative Correct Answer Badge */}
                      {showCorrectFeedback && !isSelected && (
                        <span className="flex items-center gap-1 rounded-md bg-green-500 px-2 py-1 font-body text-[10px] text-white sm:text-xs">
                          <Check className="h-3 w-3" />
                          <span className="hidden sm:inline">Also correct</span>
                        </span>
                      )}
                      {/* Video Badge */}
                      {hasVideo && (
                        <span className="flex items-center justify-center rounded-md bg-livelinx-blue300 px-2 py-1 font-body text-[10px] text-white sm:text-xs">
                          <Video className="h-3 w-3 sm:h-4 sm:w-4" />
                        </span>
                      )}
                    </div>

                    <div className="flex items-start gap-2 sm:gap-4">
                      {/* Checkbox */}
                      <Checkbox
                        checked={isSelected || showCorrectFeedback || showIncorrectFeedback}
                        feedbackState={(() => {
                          if (showCorrectFeedback) return 'correct';
                          if (showIncorrectFeedback) return 'incorrect';
                          return 'default';
                        })()}
                        onCheckedChange={() =>
                          !isValidated &&
                          !previousCorrectAnswerId &&
                          !isIncorrectAttempt &&
                          handleAnswerToggle(item.id)
                        }
                        disabled={isValidated || !!previousCorrectAnswerId || isIncorrectAttempt}
                        className="mt-1 h-5 w-5 shrink-0 sm:h-6 sm:w-6"
                        onClick={(e) => e.stopPropagation()}
                        aria-label={t('common.aria.selectAnswer', {
                          title: item.title,
                        })}
                      />

                      {/* Content */}
                      <div className="min-w-0 flex-1">
                        <div className="mb-3 flex items-start gap-2">
                          <Label
                            htmlFor={item.id}
                            className="flex-1 cursor-pointer pr-16 font-body text-base font-semibold text-livelinx-text sm:pr-24 sm:text-lg"
                          >
                            {parseHtml(item.title)}
                          </Label>
                        </div>

                        {item.description && (
                          <p className="mb-3 font-body text-sm text-livelinx-grey200 sm:text-base">
                            {parseHtml(item.description)}
                          </p>
                        )}

                        {/* Assets */}
                        {hasAssets && (
                          <div className="space-y-2">
                            <div className="flex flex-wrap gap-3">
                              {itemAssets.map((asset, assetIndex) => {
                                const assetUrl = getAssetUrl(asset, assetsBaseUrl);
                                return (
                                  <div
                                    key={assetIndex}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setViewerState({
                                        isOpen: true,
                                        assets: itemAssets,
                                        initialIndex: assetIndex,
                                        title: item.title,
                                      });
                                    }}
                                    className="h-24 w-24 cursor-pointer overflow-hidden rounded-lg border-2 border-livelinx-purple100 bg-livelinx-grey100 shadow-sm transition-all duration-300 hover:scale-105 hover:border-livelinx-purple200 hover:shadow-md sm:h-28 sm:w-28"
                                  >
                                    <ImageWithLoader
                                      src={assetUrl}
                                      alt={asset.title}
                                      className="h-full w-full object-contain p-1"
                                      containerClassName="w-full h-full"
                                      skeletonClassName="w-full h-full"
                                      loading="lazy"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                            <p className="text-xs italic text-livelinx-grey200">
                              {t('common.messages.clickToEnlarge')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Info items (if any) */}
          {infoItems.length > 0 && (
            <div className="mt-4 sm:mt-6">
              <h2 className="mb-3 font-heading text-lg text-livelinx-text sm:text-xl">
                {t('caseViewer.question.referenceDocuments')}
              </h2>
              <div className="rounded-xl bg-livelinx-blue50 p-4 sm:p-5">
                <div
                  className={cn(
                    'grid grid-cols-1 justify-items-center gap-3 sm:gap-4',
                    referenceDocumentsGridCols
                  )}
                >
                  {infoItems.map((item, itemIndex) => {
                    const { assets: itemAssets, title } = resolveItemAssets(
                      item,
                      assets,
                      itemIndex
                    );
                    return (
                      <InfoCard
                        key={`item-${itemIndex}`}
                        title={title}
                        description={item.description}
                        assets={itemAssets}
                        onOpenViewer={handleOpenViewer}
                        assetsBaseUrl={assetsBaseUrl}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Asset Viewer */}
      {viewerState && (
        <AssetViewer
          isOpen={viewerState.isOpen}
          onClose={() => setViewerState(null)}
          assets={viewerState.assets.map((asset) => {
            // Enrichir les assets avec speakerVideoRef depuis answerMap
            const answerWithVideo = answerItems.find(
              (answer) => answer.assetRefs.includes(asset.id) && !!answerMap[answer.id]?.videoRef
            );

            return {
              ...asset,
              speakerVideoRef: answerWithVideo
                ? answerMap[answerWithVideo.id].videoRef!
                : undefined,
            };
          })}
          assetsBaseUrl={assetsBaseUrl}
          initialIndex={viewerState.initialIndex}
          title={viewerState.title}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        selectedAnswerText={selectedAnswerText}
        onClose={handleCancel}
        onConfirm={handleConfirm}
      />
    </>
  );
};
